import { useState, useEffect } from "react";

/**
 * Custom hook for debouncing values
 * Useful for real-time validation and search
 */
export function useDebounce<T>(value: T, delayMs: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up the timeout
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    // Clean up the timeout if value changes before delay ends
    return () => clearTimeout(handler);
  }, [value, delayMs]);

  return debouncedValue;
}

/**
 * Custom hook for debouncing functions
 * Useful for API calls triggered by input changes
 */
export function useDebounceFn<T extends (...args: any[]) => any>(fn: T, delayMs: number = 500): T {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debounced = ((...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = setTimeout(() => {
      fn(...args);
    }, delayMs);

    setTimeoutId(newTimeoutId);
  }) as T;

  return debounced;
}

/**
 * Hook for real-time field validation with debounce
 */
export function useValidateField(
  validateFn: (value: string) => Promise<string | null>,
  delayMs: number = 500
) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const debouncedValue = useDebounce(value, delayMs);

  useEffect(() => {
    if (!debouncedValue) {
      setError(null);
      return;
    }

    const validate = async () => {
      setIsValidating(true);
      try {
        const validationError = await validateFn(debouncedValue);
        setError(validationError);
      } catch (err) {
        setError("Erro ao validar campo");
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [debouncedValue, validateFn]);

  return {
    value,
    setValue,
    error,
    isValidating,
  };
}
